import { useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { cn } from '#client/lib/cn';
import { Spinner } from '#client/components/ui/Spinner';
import { useDelayedFlag } from '#client/hooks/useDelayedFlag';
import { useAuth } from '#client/providers/AuthProvider';
import { CommandPaletteProvider } from '#client/features/command-palette';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { routes } from '#client/lib/routes';

/**
 * Authenticated shell: sidebar, top bar and the routed page.
 *
 * It also gates access: without a session every route redirects to the login
 * page, rather than merely hiding the chrome.
 */
export function AppLayout() {
  const { user, initializing } = useAuth();
  const location = useLocation();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const showBootSpinner = useDelayedFlag(initializing, 250);

  if (initializing) {
    // GET /auth/me against a local server settles in milliseconds, so showing
    // a spinner immediately would make every load flicker. Below 250ms this
    // renders nothing at all.
    return showBootSpinner ? (
      <div className="c-app__loading">
        <Spinner size="lg" label="Loading NDC" showLabel />
      </div>
    ) : null;
  }

  if (!user) {
    // Remember where they were headed so login can send them back.
    return <Navigate to={routes.login} replace state={{ from: location }} />;
  }

  // Inside the auth gate: the palette searches records, so it has no meaning
  // for a signed-out visitor and its shortcut should not be live on /login.
  return (
    <CommandPaletteProvider>
      <div className="c-app">
        <div
          className={cn('c-app__sidebar', navigationOpen && 'c-app__sidebar--open')}
          role="presentation"
        >
          <Sidebar onNavigate={() => setNavigationOpen(false)} />
        </div>

        {navigationOpen && (
          <button
            type="button"
            className="c-app__scrim"
            aria-label="Close navigation"
            onClick={() => setNavigationOpen(false)}
          />
        )}

        <div className="c-app__main">
          <TopBar onToggleNavigation={() => setNavigationOpen((open) => !open)} />
          <main className="c-app__content">
            <Outlet />
          </main>
        </div>
      </div>
    </CommandPaletteProvider>
  );
}
